import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@cargomart/ui'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, QueryDefinition } from '@reduxjs/toolkit/dist/query'
import { QueryActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate'
import IconAttach from 'assets/attach.svg?react'
import IconPlus from 'assets/plus.svg?react'
import cn from 'classnames'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { ModalSimple } from 'components/ModalSimple'
import { escapeRegExp } from 'helpers/string'
import { useIsMobile } from 'hooks/media'
import { getContactFullName, getContactFullPhone } from 'modules/contact/helpers'
import { TContact } from 'modules/contact/types'
import { ListRow } from 'src/tailwind-components/extends/List'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'
import Search from 'src/tailwind-components/extends/Search/Search'

type TProps = {
  contacts: TContact[] | undefined
  handleOpenContactAddModal: () => void
  handleSearchContact: (search: string) => void
  isContactListFetching: boolean
  isContactListLoading: boolean
  searchQuery: string
  classNameContainer?: string
  customerId?: string
  handleAttach?: (customerId: string, contactId: string) => void
  isAccessAddContact?: boolean
  isShowAttachBtn?: boolean
  refetchContacts?: () => QueryActionCreatorResult<
    QueryDefinition<string, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, TContact[], 'api'>
  >
  setIsAttachBlockFullHeight?: React.Dispatch<React.SetStateAction<boolean>>
  setIsShowAttachBtn?: React.Dispatch<React.SetStateAction<boolean>>
}

const AttachBlock = ({
  classNameContainer,
  customerId,
  isAccessAddContact,
  contacts,
  isContactListLoading,
  isContactListFetching,
  searchQuery,
  handleSearchContact,
  refetchContacts,
  setIsAttachBlockFullHeight,
  isShowAttachBtn,
  setIsShowAttachBtn,
  handleOpenContactAddModal,
  handleAttach
}: TProps) => {
  const isMobile = useIsMobile()
  const { closeModal, openModal } = useModal()

  const skeleton = [...new Array(1)].map((_, i) => <PlaceholderCell key={i} className="mb-2.5 px-4 py-0" height={52} />)

  const empty = 'Контакты не найдены'
  const searchPlaceholder = 'ФИО / телефон контакта'

  const [isOutside, setIsOutside] = useState(false)
  const listRef = useRef<HTMLDivElement | null>(null)

  const checkPosition = () => {
    if (listRef.current) {
      const rect = listRef.current.getBoundingClientRect()
      if (rect.right > window.innerWidth || rect.bottom > window.innerHeight || rect.left < 0 || rect.top < 0) {
        setIsOutside(true)
      } else {
        setIsOutside(false)
      }
    }
  }

  const handleShowContactList = () => {
    isMobile && openModal(MODAL_IDS.AttachContact)
    checkPosition()
    handleSearchContact('')
    setIsShowAttachBtn && setIsShowAttachBtn(false)
    refetchContacts && refetchContacts()
    setIsAttachBlockFullHeight && setIsAttachBlockFullHeight(true)
    setIsOutside(false)
  }

  const handleHideContactList = () => {
    setIsShowAttachBtn && setIsShowAttachBtn(true)
    setIsAttachBlockFullHeight && setIsAttachBlockFullHeight(false)
    setIsOutside(false)
    handleSearchContact('')
    closeModal(MODAL_IDS.AttachContact)
  }

  const highlightText = (text: string, searchType: string) => {
    if (!searchType) {
      return text
    }

    return text.split(new RegExp(`(${escapeRegExp(searchType)})`, 'gi')).map((part, index) =>
      part.toLowerCase() === searchType.toLowerCase() ? (
        <mark key={index} style={{ backgroundColor: '#F9E197' }}>
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  useLayoutEffect(() => {
    checkPosition()
    window.addEventListener('resize', checkPosition)
    window.addEventListener('scroll', checkPosition)

    return () => {
      window.removeEventListener('resize', checkPosition)
      window.removeEventListener('scroll', checkPosition)
    }
  }, [isShowAttachBtn])

  useEffect(() => {
    setIsShowAttachBtn && setIsShowAttachBtn(true)
  }, []) // eslint-disable-line

  return (
    <div className={cn('w-full px-3.5 py-2', classNameContainer)}>
      {isAccessAddContact && isShowAttachBtn ? (
        <Button
          data-test-id="customers-page--customer-detail--contact-attach-btn"
          onClick={handleShowContactList}
          size="nm"
          variant="text"
          className={cn(
            'text-sm w-full !justify-start !p-0 font-normal leading-5 text-[#0076e1]',
            'hover:bg-[#f0f3f7]',
            '[&_span]:flex [&_span]:items-center [&_span]:gap-2'
          )}
        >
          <IconAttach size="xs" />
          Привязать контакт
        </Button>
      ) : (
        <>
          {isMobile ? (
            <ModalItem id={MODAL_IDS.AttachContact}>
              <ModalSimple
                bodyClassName="flex flex-col"
                id={MODAL_IDS.AttachContact}
                onClose={handleHideContactList}
                size="nm"
                title={isContactListLoading ? <PlaceholderCell height={32} /> : 'Выберите контакт'}
              >
                <div className="flex h-full flex-grow flex-col">
                  <div
                    className={cn(
                      'relative z-[2]',
                      'max-md:flex max-md:max-h-[calc(100%-48px)] max-md:grow max-md:flex-col'
                    )}
                  >
                    <Search
                      isInDetailPage
                      handleSearch={handleSearchContact}
                      placeholder={searchPlaceholder}
                      searchQuery={searchQuery || ''}
                    />
                    <div
                      className={cn(
                        'absolute left-0 z-[5] w-full rounded-lg bg-[#fff]',
                        'max-md:absolute max-md:inset-x-0 max-md:bottom-5 max-md:top-10 max-md:order-2 max-md:overflow-auto',
                        'md:shadow-[0_0_16px_rgba(0,0,0,0.12),0_4px_8px_rgba(0,0,0,0.12),0_0_1px_rgba(0,0,0,0.2)]'
                      )}
                    >
                      <div
                        className={cn(
                          'h-full max-h-[calc(100vh/3)] w-full overflow-y-scroll p-1',
                          'max-md:shadow-none max-md:static max-md:max-h-[unset]'
                        )}
                      >
                        {/* eslint-disable-next-line */}
                        {!isContactListFetching ? (
                          contacts?.length ? (
                            contacts?.map((contact) => (
                              <ListRow
                                key={contact.id}
                                onClick={() => handleAttach && customerId && handleAttach(customerId, contact.id)}
                                className={cn(
                                  'flex flex-col items-start justify-between rounded px-4 py-2 text-[rgba(0,0,0,0.72)] transition-[0.3s]',
                                  'hover:bg-[rgba(26,28,30,0.1)] hover:transition-[0.3s]',
                                  'before:hidden after:hidden',
                                  'max-md:border-b-[thin] max-md:border-solid max-md:border-b-[rgba(0,0,0,0.12)] max-md:px-0 max-md:py-2'
                                )}
                              >
                                <div className="text-sm line-clamp-1 w-full overflow-hidden text-ellipsis font-normal leading-5 text-[#1a1c1e]">
                                  {highlightText(getContactFullName(contact), searchQuery)}
                                </div>
                                <div className="w-full text-body-sm font-normal leading-4 text-[#6e7076]">
                                  {highlightText(getContactFullPhone(contact), searchQuery)}
                                </div>
                              </ListRow>
                            ))
                          ) : (
                            <div className="p-4 text-center">{empty}</div>
                          )
                        ) : (
                          skeleton
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto w-full justify-center bg-[#0087ff] p-0">
                    <div className={cn('px-4 py-2', 'max-md:text-white max-md:h-full max-md:w-full max-md:p-0')}>
                      <Button
                        className="text-sm h-max w-full py-3.5 leading-5"
                        data-test-id="customers-page--customer-detail--create-contact-btn"
                        onClick={handleOpenContactAddModal}
                        size="nm"
                        variant="solid"
                      >
                        Создать новый контакт
                      </Button>
                    </div>
                  </div>
                </div>
              </ModalSimple>
            </ModalItem>
          ) : (
            <>
              <div className="fixed inset-0 z-[1]" onClick={handleHideContactList} />
              <div
                className={cn(
                  'relative z-[2]',
                  'max-md:flex max-md:max-h-[calc(100%-48px)] max-md:grow max-md:flex-col'
                )}
              >
                <Search
                  isInDetailPage
                  handleSearch={handleSearchContact}
                  placeholder={searchPlaceholder}
                  searchQuery={searchQuery || ''}
                />
                <div
                  ref={listRef}
                  className={cn(
                    'absolute left-0 z-[5] w-full rounded-lg bg-[#fff]',
                    'max-md:absolute max-md:inset-x-0 max-md:bottom-5 max-md:top-10 max-md:order-2 max-md:overflow-auto',
                    'md:shadow-[0_0_16px_rgba(0,0,0,0.12),0_4px_8px_rgba(0,0,0,0.12),0_0_1px_rgba(0,0,0,0.2)]',
                    {
                      'bottom-[33px]': isOutside
                    }
                  )}
                >
                  <div
                    className={cn(
                      'h-full max-h-[calc(100vh/3)] w-full overflow-y-scroll p-1',
                      'max-md:static max-md:max-h-[unset]'
                    )}
                  >
                    {/* eslint-disable-next-line */}
                    {!isContactListFetching ? (
                      contacts?.length ? (
                        contacts?.map((contact) => (
                          <ListRow
                            key={contact.id}
                            onClick={() => handleAttach && customerId && handleAttach(customerId, contact.id)}
                            className={cn(
                              'flex flex-col items-start justify-between rounded px-4 py-2 text-[rgba(0,0,0,0.72)] transition-[0.3s]',
                              'hover:bg-[rgba(26,28,30,0.1)] hover:transition-[0.3s]',
                              'before:hidden after:hidden',
                              'max-md:border-b-[thin] max-md:border-solid max-md:border-b-[rgba(0,0,0,0.12)] max-md:px-0 max-md:py-2'
                            )}
                          >
                            <div className="text-sm line-clamp-1 w-full overflow-hidden text-ellipsis font-normal leading-5 text-[#1a1c1e]">
                              {highlightText(getContactFullName(contact), searchQuery)}
                            </div>
                            <div className="w-full text-body-sm font-normal leading-4 text-[#6e7076]">
                              {highlightText(getContactFullPhone(contact), searchQuery)}
                            </div>
                          </ListRow>
                        ))
                      ) : (
                        <div className="p-4 text-center">{empty}</div>
                      )
                    ) : (
                      skeleton
                    )}
                  </div>
                  <div className="h-px w-[calc(100%-8px)] bg-[#e0e2ec]" />
                  <div className={cn('px-4 py-2', 'max-md:text-white max-md:h-full max-md:w-full max-md:p-0')}>
                    <Button
                      data-test-id="customers-page--customer-detail--create-contact-btn"
                      onClick={handleOpenContactAddModal}
                      size="nm"
                      variant="text"
                      className={cn(
                        'text-sm w-full !justify-start p-0 font-normal leading-5 text-[#0076e1]',
                        'hover:bg-[#f0f3f7]',
                        '[&_span]:flex [&_span]:items-center [&_span]:gap-2'
                      )}
                    >
                      <IconPlus size="xs" />
                      Создать новый контакт
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default AttachBlock
